// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import type { State } from "@store/reducers";
import { getUserId, hasAiTeamPermission, isClientAdmin } from "@modules/account/selectors";
import clientActionCreators from "@modules/client/actionCreator";
import * as clientsSelectors from "@modules/client/selectors";
import { Loader, View } from "@components";
import { getRouteClientIdParam } from "@selectors";
import {
  Organisation,
  ServiceAbsenceMainOptions,
  ServiceAbsenceAdditionalOptions,
  SlaMeasures,
  ChangeSlaModal,
} from "@modules/client/components";
import type { Client as ClientType } from "@modules/client/types";
import withErrorHandler from "@components/error";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import type {
  ClientServiceAbsenceManagement,
  AbsenceManagementSettings,
  AITeamSettings,
} from "@modules/client/reducer/service/types";
import type { SlaAgreement } from "@modules/client/reducer/sla/types";
import { parseSchema } from "@utils/schema";

type PropsFromState = $Exact<{
  isLoading: boolean,
  clientId: number | null,
  currentUserId: number | null,
  currentClient: ClientType | null,
  service: ClientServiceAbsenceManagement,
  serviceSchema: Object,
  mainOptionsSchema: Object | null,
  additionalOptionsSchema: Object | null,
  aiTeamSettings: AITeamSettings | null,
  showMainOptionsModal: boolean,
  settings: AbsenceManagementSettings,
  showAdditionalOptionsModal: boolean,
  agreements: SlaAgreement[],
  slaSchema: Object | null,
  activeSlaModal: string | null,
  isClientAdmin: boolean,
  hasAiTeamPermission: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getClient: typeof clientActionCreators.GetClient,
  getClientService: typeof clientActionCreators.GetServiceAbsenceManagementSettings,
  updateService: typeof clientActionCreators.UpdateServiceAbsenceManagementSettings,
  getClientAITeamSettings: typeof clientActionCreators.GetClientAITeamSettings,
  updateClientAITeamSettings: typeof clientActionCreators.UpdateClientAITeamSettings,
  getAgreements: typeof clientActionCreators.GetAgreements,
  getSlaSchema: typeof clientActionCreators.GetSlaSchema,
  toggleSlaModal: typeof clientActionCreators.ToggleSlaClientModal,
}>;

type Props = $Exact<{
  ...PropsFromPermissions,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type OwnState = $Exact<{
  schema: Object | null,
}>;

export class ClientDetailsPage extends React.Component<Props, OwnState> {
  state = {
    schema: null,
  };

  componentDidMount = async () => {
    this.props.getClient({ clientId: this.props.clientId });
    this.props.getClientService({
      clientId: this.props.clientId,
    });
    this.props.getClientAITeamSettings({
      clientId: this.props.clientId,
    });

    this.props.getAgreements({ clientId: this.props.clientId });
    this.props.getSlaSchema({ clientId: this.props.clientId });

    const schema = await parseSchema("ClientDetailsDTO", {
      clientId: this.props.clientId,
    });
    this.setState({ schema });
  };

  updateServiceData = (formData: ClientServiceAbsenceManagement) => {
    this.props.updateService({
      clientId: this.props.clientId,
      formData,
    });
  };

  updateAITeamSettings = (data: AITeamSettings) => {
    this.props.updateClientAITeamSettings({
      clientId: this.props.clientId,
      data,
    });
  };

  toggleSlaModal = (slaName: string) => {
    this.props.toggleSlaModal(slaName);
  };

  get disableEdit() {
    return !!this.props.isClientAdmin;
  }

  render() {
    const { service, mainOptionsSchema, additionalOptionsSchema } = this.props;
    return (
      <>
        {this.props.isLoading && <Loader />}
        <View gap={6}>
          {this.state.schema && this.props.currentClient && (
            <Organisation
              clientId={this.props.clientId}
              client={this.props.currentClient}
              schema={this.state.schema}
              disableEdit={this.disableEdit}
            />
          )}
          <View direction="row" gap="6">
            <View className="gs-container" flex={1}>
              {/* Main options */}
              <ServiceAbsenceMainOptions
                submitForm={this.updateServiceData}
                schema={mainOptionsSchema}
                service={service}
                disableEdit={this.disableEdit}
              />
            </View>
            <View className="gs-container" flex={1}>
              {/* Additional options */}
              <ServiceAbsenceAdditionalOptions
                submitForm={this.updateServiceData}
                service={service}
                schema={additionalOptionsSchema}
                disableEdit={this.disableEdit}
              />
            </View>
          </View>
          <View direction="row" gap="6">
            <View className="gs-container" flex={1}>
              {/* SLA measures */}
              <SlaMeasures
                agreements={this.props.agreements}
                openModal={this.toggleSlaModal}
                disableEdit={this.disableEdit}
              />
            </View>
          </View>
        </View>
        {this.props.slaSchema && !!this.props.activeSlaModal && (
          <ChangeSlaModal clientId={this.props.clientId} schema={this.props.slaSchema} />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => {
  const clientId = ownProps.clientId || getRouteClientIdParam(ownProps);

  return {
    isLoading: clientsSelectors.isLoading(state),
    clientId,
    currentUserId: getUserId(state),
    currentClient: clientsSelectors.getClient(state),
    service: clientsSelectors.getService(state),
    serviceSchema: clientsSelectors.getServiceSchema(state),
    mainOptionsSchema: clientsSelectors.getMainOptionsSchema(state),
    additionalOptionsSchema: clientsSelectors.getAdditionalOptionsSchema(state),
    aiTeamSettings: clientsSelectors.getClientAITeamSettings(state),
    showMainOptionsModal: clientsSelectors.getShowMainOptionsModal(state),
    settings: clientsSelectors.getServiceSettings(state),
    showAdditionalOptionsModal: clientsSelectors.getShowAdditionalOptionsModal(state),
    agreements: clientsSelectors.getAgreements(state),
    slaSchema: clientsSelectors.getSlaSchema(state),
    activeSlaModal: clientsSelectors.getActiveSlaModal(state),
    isClientAdmin: isClientAdmin(state),
    hasAiTeamPermission: hasAiTeamPermission(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getClient: clientActionCreators.GetClient,
  getClientService: clientActionCreators.GetServiceAbsenceManagementSettings,
  updateService: clientActionCreators.UpdateServiceAbsenceManagementSettings,
  getClientAITeamSettings: clientActionCreators.GetClientAITeamSettings,
  updateClientAITeamSettings: clientActionCreators.UpdateClientAITeamSettings,
  getAgreements: clientActionCreators.GetAgreements,
  getSlaSchema: clientActionCreators.GetSlaSchema,
  toggleSlaModal: clientActionCreators.ToggleSlaClientModal,
};

export default compose(
  withErrorHandler({}),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({ permissionId: PermissionEnum.ACCESS_ADMIN_CLIENT }),
)(ClientDetailsPage);
